<script>
import Conditions from '@/components/customer/modal/Conditions.vue'

export default {
  name: 'CustomerSignUp',
  components: {
    Conditions
  },
  data () {
    return {
      email: '',
      password: '',
      passwordConfirm: '',
      firstName: '',
      lastName: '',
      mobile: '',
      country: '',
      termsAccepted: false,
      conditionsModalShow: false,
      showPassword: false,
      showPasswordConfirm: false,
      isSubmitting: false,
      isCountryLoading: false,
      countryLoadError: '',
      submitError: ''
    }
  },
  computed: {
    countryList () {
      return this.$store.getters.getNationCodeList
    }
  },
  methods: {
    requiredRule (value) {
      return String(value || '').trim().length > 0 || '필수 입력 항목입니다.'
    },
    emailRule (value) {
      const email = String(value || '').trim()
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || '올바른 이메일을 입력해 주세요.'
    },
    passwordRule (value) {
      const password = String(value || '')
      if (Array.from(password).length < 8) return '비밀번호는 8자 이상이어야 합니다.'
      if (new TextEncoder().encode(password).length > 72) return '비밀번호는 72바이트 이하여야 합니다.'
      return true
    },
    passwordConfirmRule (value) {
      return value === this.password || '비밀번호가 일치하지 않습니다.'
    },
    mobileRule (value) {
      const digits = String(value || '').replace(/[^0-9]/g, '')
      return /^\d{10,11}$/.test(digits) || '휴대폰번호는 숫자 10자리 또는 11자리로 입력해 주세요.'
    },
    normalizeMobile (value) {
      const digits = String(value || '').replace(/[^0-9]/g, '').slice(0, 11)
      this.mobile = this.$formatPhoneNumber(digits, 'mobile')
      this.submitError = ''
    },
    acceptTerms () {
      this.termsAccepted = true
      this.conditionsModalShow = false
      this.submitError = ''
    },
    getApiErrorMessage (error, fallback) {
      const details = error?.response?.data?.details
      if (Array.isArray(details) && details[0]?.message) return details[0].message
      return error?.response?.data?.message || fallback
    },
    async signUp () {
      if (this.isSubmitting) return

      this.submitError = ''
      const isValid = await this.$refs.signUpForm.validate()
      if (!isValid) {
        this.submitError = '입력한 회원정보를 확인해 주세요.'
        return
      }
      if (!this.termsAccepted) {
        this.submitError = '이용약관 및 개인정보 수집·이용에 동의해 주세요.'
        return
      }

      this.isSubmitting = true
      let accountCreated = false

      try {
        await this.$http({
          method: 'POST',
          url: '/api/cust/signUp',
          data: {
            email: this.email.trim(),
            password: this.password,
            firstName: this.firstName.trim(),
            lastName: this.lastName.trim(),
            mobile: this.mobile,
            country: this.country,
            termsAccepted: this.termsAccepted
          },
          name: '[ Axios / 회원가입 ]',
          skipSpinner: true
        })
        accountCreated = true

        const loginResponse = await this.$http({
          method: 'POST',
          url: '/api/auth/signIn',
          data: {
            email: this.email.trim(),
            pwd: this.password
          },
          name: '[ Axios / 회원가입 후 로그인 ]',
          skipSpinner: true
        })

        this.$store.dispatch('setCustomerInfo', loginResponse.data)
        this.$q.notify({ type: 'positive', message: '회원가입이 완료되었습니다.' })
        await this.$router.replace('/')
      } catch (error) {
        if (accountCreated) {
          this.$q.notify({
            type: 'warning',
            message: '회원가입은 완료되었지만 자동 로그인하지 못했습니다. 다시 로그인해 주세요.'
          })
          await this.$router.replace('/login')
          return
        }

        this.submitError = this.getApiErrorMessage(error, '회원가입 처리 중 오류가 발생했습니다.')
      } finally {
        this.isSubmitting = false
      }
    },
    async loadCountries () {
      this.isCountryLoading = true
      this.countryLoadError = ''

      try {
        await this.$getCodeList('NATION')
        if (!this.countryList.length) {
          this.countryLoadError = '선택할 수 있는 국가가 없습니다.'
        }
      } catch (error) {
        this.$log.debug('[ 회원가입 / 국가 목록 조회 실패 ]', error)
        this.countryLoadError = '국가 목록을 불러오지 못했습니다.'
      } finally {
        this.isCountryLoading = false
      }
    }
  },
  mounted () {
    if (this.$store.getters.getSessionStatus) {
      this.$router.replace('/')
      return
    }
    this.loadCountries()
  }
}
</script>

<template>
  <q-page class="signup-page row items-center justify-center q-pa-md">
    <q-card class="signup-card">
      <section class="signup-header">
        <img
          class="signup-logo"
          :src="require('@/assets/imgs/common/logo.png')"
          alt="AONE Logistics">
        <div>
          <div class="text-overline text-primary">AONE ACCOUNT</div>
          <h1>회원가입</h1>
          <p>배송 조회와 물류 서비스를 이용할 고객 계정을 만듭니다.</p>
        </div>
      </section>

      <q-separator />

      <q-form
        ref="signUpForm"
        class="signup-form"
        @submit.prevent="signUp">
        <section class="form-section">
          <div class="section-title">
            <q-icon name="account_circle" />
            <span>계정 정보</span>
          </div>

          <q-input
            v-model="email"
            outlined
            type="email"
            label="이메일 *"
            autocomplete="username"
            maxlength="254"
            lazy-rules
            :disable="isSubmitting"
            :rules="[requiredRule, emailRule]"
            @update:model-value="submitError = ''">
            <template #prepend>
              <q-icon name="mail_outline" />
            </template>
          </q-input>

          <div class="row q-col-gutter-md">
            <div class="col-12 col-sm-6">
              <q-input
                v-model="password"
                outlined
                :type="showPassword ? 'text' : 'password'"
                label="비밀번호 *"
                autocomplete="new-password"
                hint="8자 이상, 72바이트 이하"
                lazy-rules
                :disable="isSubmitting"
                :rules="[passwordRule]"
                @update:model-value="submitError = ''">
                <template #prepend>
                  <q-icon name="lock_outline" />
                </template>
                <template #append>
                  <q-icon
                    class="cursor-pointer"
                    :name="showPassword ? 'visibility_off' : 'visibility'"
                    @click="showPassword = !showPassword" />
                </template>
              </q-input>
            </div>
            <div class="col-12 col-sm-6">
              <q-input
                v-model="passwordConfirm"
                outlined
                :type="showPasswordConfirm ? 'text' : 'password'"
                label="비밀번호 확인 *"
                autocomplete="new-password"
                lazy-rules
                :disable="isSubmitting"
                :rules="[requiredRule, passwordConfirmRule]"
                @update:model-value="submitError = ''">
                <template #prepend>
                  <q-icon name="verified_user" />
                </template>
                <template #append>
                  <q-icon
                    class="cursor-pointer"
                    :name="showPasswordConfirm ? 'visibility_off' : 'visibility'"
                    @click="showPasswordConfirm = !showPasswordConfirm" />
                </template>
              </q-input>
            </div>
          </div>
        </section>

        <section class="form-section">
          <div class="section-title">
            <q-icon name="badge" />
            <span>기본 정보</span>
          </div>

          <div class="row q-col-gutter-md">
            <div class="col-12 col-sm-6">
              <q-input
                v-model="lastName"
                outlined
                label="성 *"
                maxlength="100"
                lazy-rules
                :disable="isSubmitting"
                :rules="[requiredRule]"
                @update:model-value="submitError = ''" />
            </div>
            <div class="col-12 col-sm-6">
              <q-input
                v-model="firstName"
                outlined
                label="이름 *"
                maxlength="100"
                lazy-rules
                :disable="isSubmitting"
                :rules="[requiredRule]"
                @update:model-value="submitError = ''" />
            </div>
          </div>

          <div class="row q-col-gutter-md">
            <div class="col-12 col-sm-7">
              <q-input
                :model-value="mobile"
                outlined
                type="tel"
                inputmode="numeric"
                maxlength="13"
                label="휴대폰번호 *"
                hint="국가번호를 제외한 숫자 10~11자리를 입력해 주세요."
                lazy-rules
                :disable="isSubmitting"
                :rules="[mobileRule]"
                @update:model-value="normalizeMobile">
                <template #prepend>
                  <q-icon name="phone_iphone" />
                </template>
              </q-input>
            </div>
            <div class="col-12 col-sm-5">
              <q-select
                v-model="country"
                outlined
                emit-value
                map-options
                label="국가 *"
                :options="countryList"
                :loading="isCountryLoading"
                :disable="isSubmitting || isCountryLoading || Boolean(countryLoadError)"
                :rules="[requiredRule]"
                @update:model-value="submitError = ''" />
              <div
                v-if="countryLoadError"
                class="field-error">
                {{ countryLoadError }}
                <q-btn flat dense color="primary" label="다시 시도" @click="loadCountries" />
              </div>
            </div>
          </div>
        </section>

        <section class="terms-section">
          <div class="row items-center justify-between no-wrap">
            <q-checkbox
              v-model="termsAccepted"
              :disable="isSubmitting"
              label="이용약관 및 개인정보 수집·이용에 동의합니다. (필수)"
              @update:model-value="submitError = ''" />
            <q-btn
              flat
              dense
              no-caps
              color="primary"
              label="내용 보기"
              @click="conditionsModalShow = true" />
          </div>
        </section>

        <div
          v-if="submitError"
          class="signup-error"
          role="alert">
          <q-icon name="error_outline" />
          <span>{{ submitError }}</span>
        </div>

        <q-btn
          class="full-width signup-submit"
          type="submit"
          color="primary"
          unelevated
          size="lg"
          label="가입하기"
          :loading="isSubmitting"
          :disable="isSubmitting || isCountryLoading || Boolean(countryLoadError)" />

        <div class="login-link">
          <span>이미 계정이 있으신가요?</span>
          <q-btn flat no-caps color="primary" label="로그인" to="/login" />
        </div>
      </q-form>
    </q-card>
  </q-page>

  <q-dialog v-model="conditionsModalShow">
    <Conditions @accept="acceptTerms" />
  </q-dialog>
</template>

<style scoped>
.signup-page {
  min-height: calc(100vh - 100px);
  background:
    radial-gradient(circle at 12% 10%, rgba(55, 149, 210, 0.2), transparent 32%),
    linear-gradient(145deg, #eef5f9 0%, #f8fafc 55%, #e8f0f5 100%);
}

.signup-card {
  width: min(780px, 100%);
  overflow: hidden;
  border-radius: 20px;
  box-shadow: 0 22px 60px rgba(16, 45, 66, 0.18);
}

.signup-header {
  display: flex;
  gap: 32px;
  align-items: center;
  padding: 36px 44px 30px;
}

.signup-logo {
  width: 128px;
  height: auto;
}

.signup-header h1 {
  margin: 2px 0 6px;
  color: #17384f;
  font-size: 2rem;
  font-weight: 700;
}

.signup-header p {
  margin: 0;
  color: #647784;
}

.signup-form {
  padding: 30px 44px 36px;
}

.form-section + .form-section {
  margin-top: 10px;
}

.section-title {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 16px;
  color: #294d65;
  font-size: 0.95rem;
  font-weight: 700;
}

.terms-section {
  margin: 8px 0 20px;
  padding: 10px 12px;
  border: 1px solid #d8e2e8;
  border-radius: 10px;
  background: #f8fbfc;
}

.field-error {
  margin-top: -12px;
  color: #b3261e;
  font-size: 0.78rem;
}

.signup-error {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 16px;
  padding: 10px 12px;
  border-radius: 8px;
  color: #b3261e;
  background: #fff2f0;
  font-size: 0.875rem;
}

.signup-submit {
  min-height: 50px;
  border-radius: 8px;
  font-weight: 700;
}

.login-link {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 16px;
  color: #647784;
  font-size: 0.875rem;
}

@media (max-width: 1023px) {
  .signup-page {
    min-height: calc(100vh - 60px);
  }
}

@media (max-width: 599px) {
  .signup-page {
    padding: 16px;
  }

  .signup-card {
    border-radius: 14px;
  }

  .signup-header {
    display: block;
    padding: 28px 24px 22px;
  }

  .signup-logo {
    width: 112px;
    margin-bottom: 22px;
  }

  .signup-form {
    padding: 24px;
  }

  .terms-section .row {
    align-items: flex-start;
  }
}
</style>
